import { coordOrNull, type FormularioDaObra } from '../formularioDaObra.js';
import { parseCurrencyToCents } from '@obras/shared';

/*
 * As etapas do cadastro de obra (design.md §Assistente em etapas, Nova obra).
 *
 * Sete, cada uma respondendo a uma pergunta: o que é a obra, sob que contrato,
 * em que prazo, quem fiscaliza, onde fica, com que cara aparece — e a revisão.
 * Só três campos são obrigatórios (denominação, número do contrato e valor); as
 * etapas sem obrigatório passam com "Continuar" mesmo vazias.
 */

export type EtapaDaObraId =
  'identificacao' | 'contrato' | 'prazos' | 'fiscalizacao' | 'localizacao' | 'capa' | 'revisao';

export const ETAPAS_DA_OBRA: readonly EtapaDaObraId[] = [
  'identificacao',
  'contrato',
  'prazos',
  'fiscalizacao',
  'localizacao',
  'capa',
  'revisao',
];

export const ROTULO_DA_ETAPA: Record<EtapaDaObraId, string> = {
  identificacao: 'Identificação',
  contrato: 'Contrato',
  prazos: 'Prazos',
  fiscalizacao: 'Fiscalização',
  localizacao: 'Localização',
  capa: 'Capa',
  revisao: 'Revisão',
};

export const CABECALHO_DA_ETAPA: Record<EtapaDaObraId, { titulo: string; descricao: string }> = {
  identificacao: {
    titulo: 'Identificação da obra',
    descricao: 'Como a obra será chamada, o que ela é e quem a demandou.',
  },
  contrato: {
    titulo: 'Contrato',
    descricao:
      'O instrumento que dá base à fiscalização: número, valor pactuado e empresa contratada.',
  },
  prazos: {
    titulo: 'Prazos',
    descricao:
      'A vigência do contrato e o prazo de execução da ordem de serviço. Podem ser informados depois, na edição da obra.',
  },
  fiscalizacao: {
    titulo: 'Fiscalização',
    descricao: 'Quem acompanha a obra, e de quanto em quanto tempo ela precisa de vistoria.',
  },
  localizacao: {
    titulo: 'Localização',
    descricao: 'Onde fica a obra. Marque o ponto no mapa e o endereço é preenchido sozinho.',
  },
  capa: {
    titulo: 'Imagem de capa',
    descricao: 'Opcional. Ao lado, a obra como vai aparecer na lista de obras.',
  },
  revisao: {
    titulo: 'Revisão',
    descricao:
      'Confira tudo antes de criar. Depois de criada, a obra pode ser editada a qualquer momento.',
  },
};

/** Em que etapa mora cada campo que o servidor pode recusar. */
export const ETAPA_DO_CAMPO: Record<string, EtapaDaObraId> = {
  name: 'identificacao',
  description: 'identificacao',
  status: 'identificacao',
  isReform: 'identificacao',
  secretariaId: 'identificacao',
  contractNumber: 'contrato',
  initialValueCents: 'contrato',
  companyId: 'contrato',
  contractStart: 'prazos',
  contractEnd: 'prazos',
  osStart: 'prazos',
  osEnd: 'prazos',
  visitIntervalDays: 'fiscalizacao',
  responsibleFiscalId: 'fiscalizacao',
  latitude: 'localizacao',
  longitude: 'localizacao',
  address: 'localizacao',
  city: 'localizacao',
  state: 'localizacao',
  coverMediaId: 'capa',
};

/**
 * O que falta ou está errado numa etapa, campo a campo — a mesma regra que o
 * servidor aplica (`createObraBodySchema`), adiantada para a tela: descobrir no
 * fim, depois de "Criar obra", que o valor não foi informado lá na segunda
 * etapa é o pior momento para avisar.
 */
export function errosDaEtapa(id: EtapaDaObraId, form: FormularioDaObra): Record<string, string> {
  const erros: Record<string, string> = {};

  if (id === 'identificacao' && form.name.trim().length < 3) {
    erros['name'] = 'Informe a denominação da obra, com pelo menos 3 caracteres.';
  }

  if (id === 'contrato') {
    const numero = form.contractNumber.trim();
    if (numero === '') erros['contractNumber'] = 'Informe o número do contrato.';
    else if (numero.length > 40) erros['contractNumber'] = 'Use no máximo 40 caracteres.';
    if ((parseCurrencyToCents(form.initialValue) ?? 0) <= 0) {
      erros['initialValueCents'] = 'Informe o valor inicial contratado.';
    }
  }

  if (id === 'prazos') {
    if (form.contractStart && form.contractEnd && form.contractEnd < form.contractStart) {
      erros['contractEnd'] = 'O encerramento não pode ser anterior ao início.';
    }
    if (form.osStart && form.osEnd && form.osEnd < form.osStart) {
      erros['osEnd'] = 'O término não pode ser anterior à emissão.';
    }
  }

  if (id === 'fiscalizacao' && form.visitIntervalDays.trim() !== '') {
    const dias = Number(form.visitIntervalDays);
    if (!Number.isInteger(dias) || dias < 1 || dias > 365) {
      erros['visitIntervalDays'] = 'Informe de 1 a 365 dias.';
    }
  }

  if (id === 'localizacao') {
    const lat = coordOrNull(form.latitude);
    const lng = coordOrNull(form.longitude);
    if (form.latitude.trim() !== '' && lat === null) erros['latitude'] = 'Latitude inválida.';
    else if (lat !== null && (lat < -90 || lat > 90)) {
      erros['latitude'] = 'A latitude vai de -90 a 90.';
    }
    if (form.longitude.trim() !== '' && lng === null) erros['longitude'] = 'Longitude inválida.';
    else if (lng !== null && (lng < -180 || lng > 180)) {
      erros['longitude'] = 'A longitude vai de -180 a 180.';
    }
    if (!erros['latitude'] && !erros['longitude'] && (lat === null) !== (lng === null)) {
      erros[lat === null ? 'latitude' : 'longitude'] =
        'Informe latitude e longitude juntas, ou marque o ponto no mapa.';
    }
    if (form.state.trim() !== '' && form.state.trim().length !== 2) {
      erros['state'] = 'Use a sigla do estado, com 2 letras.';
    }
  }

  return erros;
}
